import { inject, Injectable } from '@angular/core'
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar'
import { groupBy, uniq } from 'lodash'
import moment from 'moment-timezone'
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  combineLatestWith,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  finalize,
  first,
  map,
  merge,
  Observable,
  of,
  pairwise,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs'

import { AppointmentModel } from '../model/appointment/appointment'
import { ChargeDescriptionMasterData } from '../model/cdm/charge-description-master'
import { EligibilityBenefits } from '../model/eligibility'
import {
  Estimate,
  EstimateCharge,
  EstimateChargeDiscountType,
  EstimateData,
  EstimateHistoryData,
  EstimateHistoryModel,
  EstimateModel,
  EstimateTemplateModel,
  resolveChargeModifiers,
} from '../model/estimate'
import { ChargeRowFormGroup } from '../model/estimate/charge-form.types'
import { AppointmentSelectOption, EstimateFormGroup } from '../model/estimate/estimate-form.types'
import { Facility } from '../model/facility'
import {
  isInsuranceRelevantToAnyAccount,
  isPerVisitInsuranceSelectionEnabled,
  PatientInsurance,
  PatientInsuranceType,
  resolveSelectedInsurance,
  selectableInsurances,
} from '../model/patient-insurance'
import { getTimeZone } from '../utils'
import { ApiService } from './api.service'
import { ChargeFormService } from './charge-form.service'
import { ClinicService } from './clinic.service'
import { CrudService } from './crud.service'
import { EstimateTemplateApiService } from './estimate-template-api.service'
import { FacilityService } from './facility.service'
import { ParamsService } from './params.service'
import { PatientDataService } from './patient-data.service'
import { PermissionsService } from './permissions.service'

@Injectable({
  providedIn: 'root',
})
export class EstimateService {
  private readonly _crudService = inject(CrudService)
  private readonly _facilityService = inject(FacilityService)
  private readonly _chargeFormService = inject(ChargeFormService)
  private readonly _permissionsService = inject(PermissionsService)
  estimateLoading$ = new BehaviorSubject<boolean>(true)
  primaryBenefitsLoading$ = new BehaviorSubject<boolean>(false)
  secondaryBenefitsLoading$ = new BehaviorSubject<boolean>(false)
  benefitsLoading$ = combineLatest([
    this.primaryBenefitsLoading$,
    this.secondaryBenefitsLoading$,
  ]).pipe(
    map(([primaryLoading, secondaryLoading]) => primaryLoading || secondaryLoading),
    distinctUntilChanged(),
    shareReplay(1),
  )
  chargesLoaded$ = new BehaviorSubject<void>(undefined)
  originalBenefits$ = new BehaviorSubject<EligibilityBenefits | null>(null)
  originalSecondaryBenefits$ = new BehaviorSubject<EligibilityBenefits | null>(null)

  primaryInsurance$ = new BehaviorSubject<PatientInsurance | null | undefined>(undefined)
  secondaryInsurance$ = new BehaviorSubject<PatientInsurance | null | undefined>(undefined)
  hasSecondaryInsurance$ = new BehaviorSubject<boolean>(false)
  patientId$ = new BehaviorSubject<string | null>(null)
  patient$ = this.patientDataService.patient$
  needsPricing$ = new BehaviorSubject<boolean>(false)
  needsPricingNotice?: MatSnackBarRef<any>
  estimate$ = new BehaviorSubject<EstimateData | null>(null)
  savedEstimateLoaded$ = new Subject<EstimateData>()
  selectedAppointmentIds$ = new BehaviorSubject<string[]>([])
  selectedVisitNumbers$ = new BehaviorSubject<number[] | undefined>(undefined)
  chargesArray = new BehaviorSubject<FormArray<FormGroup<ChargeRowFormGroup>> | null>(null)
  chargesArray$ = this.chargesArray.asObservable()
  visitNumbersValid = true
  checkCalcDisabledState$ = new Subject<void>()
  finalizeDisabled = new BehaviorSubject<boolean>(true)
  finalizeDisabled$ = combineLatest([
    this.estimateLoading$.asObservable(),
    this.finalizeDisabled.asObservable(),
  ]).pipe(map(([loading, disabled]) => loading || disabled ))
  calcAndSaveDisabled = new BehaviorSubject<boolean>(true)
  calcAndSaveDisabled$ = combineLatest([
    this.calcAndSaveDisabled.asObservable().pipe(startWith(false)),
    this.needsPricing$.asObservable().pipe(startWith(false)),
  ]).pipe(
    map(([calcAndSaveDisabled, needsPricing]) => {
      const disabled = calcAndSaveDisabled || !needsPricing
      if (!disabled && !this.needsPricingNotice) {
        this.openPricingNotice()
      } else if (disabled && this.needsPricingNotice) {
        this.needsPricingNotice.dismiss()
        this.needsPricingNotice = undefined
      }

      this.finalizeDisabled.next(needsPricing)
      return disabled
    }),
  )
  resetByAppointments$ = new BehaviorSubject<boolean>(false)
  chargesLoading$ = new BehaviorSubject<boolean>(false)
  modelClaimSearchQuery$ = new BehaviorSubject<string | null>(null)
  pricingNoticeAction$ = new Subject<void>()

  /**
   * This is not the most elegant solution, but we need a way to trigger
   * re-evaluation of the appointmentsChanged$ observable when claim search
   * enabling changes, because model claim search query depends on it.
   */
  claimSearchEnabledChanged$ = new BehaviorSubject<boolean>(false)
  refreshAppointments$ = new Subject<void>()

  estimateChargesLoading$ = this.estimateLoading$.pipe(
    startWith(false),
    combineLatestWith(this.chargesLoading$),
    map(
      ([estimateLoading, chargesLoading]) => estimateLoading || chargesLoading,
    ),
  )

  templateFilterCtrl = new FormControl('')
  private selectedTemplateCategoryId$ = new BehaviorSubject<string>('root')

  estimateForm: EstimateFormGroup = this.fb.group({
    facilityId: this.fb.control<string | null>(null, Validators.required),
    templateId: this.fb.control<string | null>(null),
    saveTemplate: this.fb.control(false),
    name: this.fb.control<string | null>(null),
    episodeId: this.fb.control<string | null>(null),
    appointmentIds: this.fb.control<string[]>([]),
    patientType: this.fb.control<Estimate.PATIENT_TYPE>(
      Estimate.PATIENT_TYPE.OutPatient,
      Validators.required,
    ),
    charges: this.fb.array<FormGroup<ChargeRowFormGroup>>([]),
  })

  private destroy$ = new Subject<void>()

  private appointmentOptionsCache: AppointmentSelectOption[] = []

  // Carries the eligibilityId that changed, so createBenefitsStream can tell "this notify is
  // about the eligibility this slot's insurance actually points at" apart from "this notify
  // is about some other insurance entirely" - see the explicitLeg$ filter there. undefined
  // means "no specific eligibility - refresh whatever's selected now" (e.g. onModify()).
  private benefitsChangedSubject = new Subject<string | undefined>()
  readonly benefitsChanged$: Observable<string | undefined> = this.benefitsChangedSubject.asObservable()
  notifyBenefitsChanged(eligibilityId?: string): void {
    this.benefitsChangedSubject.next(eligibilityId)
  }

  constructor(
    private apiService: ApiService,
    private estimateTemplateApiService: EstimateTemplateApiService,
    private paramsService: ParamsService,
    private patientDataService: PatientDataService,
    private clinicService: ClinicService,
    private fb: FormBuilder,
    public snackBar: MatSnackBar,
  ) {
    this.patientDataService.patientId$.subscribe((id) =>
      this.patientId$.next(id),
    )

    this.estimateForm.controls.charges.valueChanges
      .pipe(
        concatMap(() =>
          this.estimate$.pipe(
            first(),
            tap((estimate) => {
              const charges = this._chargeFormService.getChargesFromFormArray(
                this.estimateForm.controls.charges,
              )
              this.estimate$.next({ ...estimate, charges })
            }),
          ),
        ),
      )
      .subscribe()

    this.appointmentsChanged$.subscribe()

    this.estimateForm.controls.appointmentIds.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (appointmentIds) => !appointmentIds || appointmentIds.length === 0,
        ),
      )
      .subscribe(() => this.modelClaimSearchQuery$.next(null))

    combineLatest([
      this.estimate$,
      this.patientDataService.insurances$,
      this.selectedAccountIds$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
      )
      .subscribe(([estimate, insurances, accountIds]) => {
        const getType = this.accountAwareTypeAccessor(accountIds)
        const scoped = (insurances ?? []).filter(i => isInsuranceRelevantToAnyAccount(i, accountIds))
        const hasSecondary = selectableInsurances(scoped, PatientInsuranceType.SECONDARY, getType).length > 0
        this.hasSecondaryInsurance$.next(hasSecondary)
        if (!estimate?.inputs) {
          return
        }

        if (!hasSecondary) {
          estimate.inputs.hasSecondary = false
          estimate.inputs.secondaryInsuranceCoversCoinsurance = false
          estimate.inputs.secondaryInsuranceCoversCopayment = false
          estimate.inputs.secondaryInsuranceCoversDeductible = false
          return
        }

        if (estimate && !estimate?.id && hasSecondary !== estimate.inputs.hasSecondary) {
          estimate.inputs.secondaryInsuranceCoversCoinsurance = hasSecondary
          estimate.inputs.secondaryInsuranceCoversCopayment = hasSecondary
          estimate.inputs.secondaryInsuranceCoversDeductible = hasSecondary
          estimate.inputs.hasSecondary = hasSecondary
        }
      })
  }

  private loadingSubjectFor(idKey: 'primaryInsuranceId' | 'secondaryInsuranceId') {
    return idKey === 'primaryInsuranceId'
      ? this.primaryBenefitsLoading$
      : this.secondaryBenefitsLoading$
  }

  benefits$ = this.createBenefitsStream(
    this.primaryInsurance$,
    this.originalBenefits$,
    'primaryInsuranceId',
  )

  secondaryBenefits$ = this.createBenefitsStream(
    this.secondaryInsurance$,
    this.originalSecondaryBenefits$,
    'secondaryInsuranceId',
  )

  private createBenefitsStream(
    insurance$: Observable<PatientInsurance | null | undefined>,
    target$: BehaviorSubject<EligibilityBenefits | null>,
    idKey: 'primaryInsuranceId' | 'secondaryInsuranceId',
  ) {
    // Seed pairwise with undefined to skip the first null resolution on reload and preserve typed benefits.
    // A notify with no eligibilityId (e.g. onModify() - "refresh whatever's
    // selected now") always matches. A notify scoped to a specific eligibility (e.g. an
    // eligibility-card save) only matches the leg whose current insurance actually points at
    // that eligibility, so editing insurance B's eligibility doesn't also refetch insurance A's
    // stream.
    const insuranceLeg$ = insurance$.pipe(
      startWith(undefined),
      pairwise(),
      filter(([previous, insurance]) => insurance !== undefined && (previous !== undefined || insurance !== null)),
      map(([previous, insurance]) => ({ insurance, insuranceRemoved: previous != null && insurance === null })),
    )
    const explicitLeg$ = this.benefitsChanged$.pipe(
      withLatestFrom(insurance$),
      filter(([, insurance]) => insurance !== undefined),
      filter(([changedEligibilityId, insurance]) =>
        changedEligibilityId == null || insurance?.eligibilityId === changedEligibilityId),
      map(([, insurance]) => ({ insurance, insuranceRemoved: false })),
    )

    // Emits a fixed { insuranceId, benefits, previousOriginal, fetchFailed } shape - not
    // { [idKey]: ... } - since a computed property key shared across two differently-shaped
    // switchMap branches defeats TS's structural inference (both branches collapse to a single
    // index signature, typing every field as the union of all fields' types across both
    // branches). idKey is still used below purely to pick the right loading subject.
    // previousOriginal reads target$ but never writes it - a consumer that can't apply this
    // emission (e.g. CONFIRMED) must not have the baseline move under it; only the consumer
    // advances target$, once it applies the fetch (see estimate.component.ts).
    return merge(insuranceLeg$, explicitLeg$).pipe(
      tap(() => this.loadingSubjectFor(idKey).next(true)),
      switchMap(({ insurance, insuranceRemoved }) => {
        const previousOriginal = target$.getValue()
        if (!insurance?.eligibilityId) {
          this.loadingSubjectFor(idKey).next(false)
          // Tagged like the populated case below, so consumers can gate this the same way.
          return of({ insuranceId: insurance?.id ?? null, benefits: null, previousOriginal, fetchFailed: false, insuranceRemoved })
        }
        const route = `/eligibilities/${insurance.eligibilityId}/benefits`
        const params = this.apiService.convertToParams()

        return this.apiService.get<EligibilityBenefits>(route, params).pipe(
          map((resp) => ({ insuranceId: insurance?.id ?? null, benefits: resp.data, previousOriginal, fetchFailed: false, insuranceRemoved })),
          finalize(() => this.loadingSubjectFor(idKey).next(false)),
          // A failed fetch must not tear down the outer stream (that would silently stop this
          // estimate from ever refreshing benefits again for the rest of the session) and must
          // not be treated as "no eligibility data" either - fetchFailed tells consumers to
          // leave whatever's currently displayed alone rather than resetting it to zero.
          catchError(() => of({ insuranceId: insurance?.id ?? null, benefits: null, previousOriginal, fetchFailed: true, insuranceRemoved })),
        )
      }),
    )
  }

  allAppointments$ = combineLatest([
    this.paramsService.patientDefined$.pipe(filter(Boolean), distinctUntilChanged()),
    this.refreshAppointments$.pipe(startWith(undefined as void)),
  ]).pipe(
    takeUntil(this.destroy$),
    switchMap(([patientId]) =>
      this.apiService
        .getAppointments({ patientId, extend: 'products,estimates,physician' })
        .pipe(map((page) => page.data || [])),
    ),
    startWith(null as AppointmentModel[] | null),
    shareReplay(1),
  )

  appointmentOptions$: Observable<AppointmentSelectOption[]> = combineLatest([
    this.allAppointments$.pipe(filter((a): a is AppointmentModel[] => a !== null)),
    this.estimate$.pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id),
    ),

  ]).pipe(
    map(([appointments, currentEstimate]: [AppointmentModel[], EstimateData] ) =>
      appointments.map((appointment: AppointmentModel, i: number): AppointmentSelectOption => {
        const hasEstimate =
          appointment.estimate != null &&
          appointment.estimateId !== currentEstimate?.id
        const selected = currentEstimate?.appointmentIds?.includes(appointment.id)
        return {
          value: appointment.id,
          viewValue: appointment.text,
          disabled: hasEstimate,
          hasEstimate,
          appointment,
          hint: this.getProcedureCodes(appointment.products),
          visitNumber: i + 1,
          selected,
        }
      }),
    ),
    // Cached so synchronous callers (setChargesOnForm) can resolve real visit numbers.
    // selectedAccountIds$ already keeps this stream hot from the constructor, so a tap piggybacks
    // on that rather than adding a second subscription.
    tap((options) => (this.appointmentOptionsCache = options)),
    startWith([]),
    shareReplay(1),
  )

  estimateHistoryLoading$ = new BehaviorSubject<boolean>(true)

  estimateHistory$ = this.estimate$.pipe(
    filter((estimate) => !!estimate?.id),
    distinctUntilKeyChanged('id'),
    tap(() => this.estimateHistoryLoading$.next(true)),
    switchMap(({ id }) =>
      this.apiService
        .get<EstimateHistoryData[]>(
          `/estimates/${encodeURIComponent(String(id ?? ''))}/history`,
        )
        .pipe(finalize(() => this.estimateHistoryLoading$.next(false))),
    ),
    map(({ data }) =>
      (data || []).map((item) => new EstimateHistoryModel(item)),
    ),
    map((logs) => {
      const grouped = groupBy(logs, (log) =>
        moment
          .tz(log.createdAt, getTimeZone(this.clinicService.clinic))
          .format('YYYY-MM-DD'),
      )
      // rewrite this in vanilla ts
      // const mapped = _map(grouped, (logs, date) => ({ date: moment.tz(date, this.tz), logs }))
      return Object.entries(grouped).map(([date, logs]) => ({
        date: moment.tz(date, getTimeZone(this.clinicService.clinic)),
        logs,
      }))
    }),
  )

  private getProcedureCodes(products: { getCPTCodes(): string[] }[]): string {
    const procedureCodes: string[] = []
    for (const product of products) {
      procedureCodes.push(...product.getCPTCodes())
    }
    return procedureCodes.join(', ')
  }

  appointmentsChanged$ = combineLatest([
    this.estimateForm.controls.appointmentIds.valueChanges,
    this.claimSearchEnabledChanged$.pipe(distinctUntilChanged()),
  ]).pipe(
    takeUntil(this.destroy$),
    // we don't care about claimSearchEnabledChanged$ value here
    // just want to re-trigger when it changes
    map(([appointmentIds]) => appointmentIds ?? []),
    switchMap((appointmentIds) => {
      if (!appointmentIds.length) {
        return of({
          appointmentIds,
          appointmentOptions: [] as AppointmentSelectOption[],
          estimate: this.estimate$.getValue(),
        })
      }

      return combineLatest({
        appointmentOptions: this.appointmentOptions$,
        estimate: this.estimate$,
        appointmentIds: of(appointmentIds),
      }).pipe(first())
    }),
    map(({ appointmentIds, appointmentOptions, estimate }) => {
      const selectedOptions = appointmentOptions
        .filter((option) => appointmentIds.includes(option.value))
      const appointments = selectedOptions.map((option) => option.appointment)
      const visitNumbers = this.selectedVisitNumbers(appointmentOptions, appointmentIds)
      const facilityId = appointments.find(
        (appointment) => appointment.locationId,
      )?.locationId
      if (facilityId != null && estimate?.facilityId == null) {
        this.estimateForm.patchValue({ facilityId })
        this.setEstimate({ ...estimate, facilityId })

        this.estimateForm.get('facilityId')?.setValue(facilityId)
      }
      this.setEstimate({ ...estimate, templateId: null })
      this.estimateForm.get('templateId')?.setValue(null)

      const cptCodes = uniq(
        appointments
          .flatMap((appointment) => appointment.products)
          .flatMap((product) => product.getCPTCodes()),
      )
      return {
        appointmentIds,
        cptCodes,
        estimate,
        visitNumbers,
      }
    }),
    map(({ appointmentIds, cptCodes, estimate, visitNumbers }) => ({
      appointmentIds,
      cptCodes,
      facilityId: this.estimateForm.get('facilityId')?.value,
      estimate,
      visitNumbers,
    })),
    combineLatestWith(this.resetByAppointments$),
    map(([{ appointmentIds, cptCodes, facilityId, estimate, visitNumbers }, resetByAppointment]) => {
      const facility = this._facilityService.allFacilities.find(
        (item) => item.id === facilityId,
      )
      const isHbFacility = !!facility?.mmx?.hbFacilityId
      const nextQuery = cptCodes.length > 0 ? cptCodes.join('|') : null
      if (isHbFacility) {
        if (!nextQuery) {
          this.modelClaimSearchQuery$.next(null)
        } else if (resetByAppointment) {
          this.modelClaimSearchQuery$.next(null)
          setTimeout(() => this.modelClaimSearchQuery$.next(nextQuery), 0)
        } else {
          this.modelClaimSearchQuery$.next(nextQuery)
        }
      } else {
        this.modelClaimSearchQuery$.next(null)
      }

      return {
        cptCodes,
        facilityId,
        estimate,
        isHbFacility,
        resetByAppointment,
        visitNumbers,
        appointmentIds,
      }
    }),
    switchMap(({
      cptCodes,
      facilityId,
      estimate,
      visitNumbers,
      isHbFacility,
      appointmentIds,
      resetByAppointment,
    }) => {
      const shouldLoadCharges =
        !isHbFacility && cptCodes.length > 0 && !!facilityId && !!appointmentIds?.length
      if (!shouldLoadCharges) {
        this.chargesLoading$.next(false)
        return of(null)
      }

      this.chargesLoading$.next(true)
      const searchParams = {
        cptCodes,
        facilityIds: facilityId ? [facilityId] : [],
      }
      return this.apiService
        .searchChargeDescriptionMaster(searchParams)
        .pipe(map((charges) => ({ charges, estimate, visitNumbers, resetByAppointment })))
    }),
    filter((result): result is {
      charges: ChargeDescriptionMasterData[]
      estimate: EstimateData | null
      visitNumbers: number[] | undefined
      resetByAppointment: boolean
    } => !!result),
    tap((
      {
        charges,
        estimate,
        visitNumbers,
        resetByAppointment,
      }: {
        charges: ChargeDescriptionMasterData[]
        estimate: EstimateData | null
        visitNumbers: number[] | undefined
        resetByAppointment: boolean
      },
    ) => {
      // Auto-populate visit numbers based on selected appointments
      // If only 1 appointment, use [1]; if multiple, assign all visit numbers
      const defaultVisitNumbers = visitNumbers?.length > 0 ? visitNumbers : [1]
      const updatedCharges: EstimateCharge[] = charges.map((c: ChargeDescriptionMasterData): EstimateCharge => {
        const { codeModifiers, ...charge } = c
        const estimateCharge = estimate.charges?.find(estimateCharge => estimateCharge.chargeCode === c.chargeCode)
        const modifiers = resolveChargeModifiers(estimateCharge, codeModifiers)
        const cptDiscountType = estimateCharge?.cptDiscountType
        const cptDiscountAmount = estimateCharge?.cptDiscountAmount
        const cptDiscountedUnits = estimateCharge?.cptDiscountedUnits
        const quantity = estimateCharge?.quantity ?? 1
        return {
          ...charge,
          modifiers,
          description: c.chargeDescription,
          quantity,
          visitNumbers: defaultVisitNumbers,
          cptDiscountType,
          cptDiscountAmount,
          cptDiscountedUnits,
        }
      },
      )
      this.chargesLoading$.next(false)
      const reset = resetByAppointment

      let finalCharges: EstimateCharge[]
      let hasChanges = false
      if (reset) {
        finalCharges = updatedCharges
        this.setEstimate({ ...estimate, charges: finalCharges })
        hasChanges = true
      } else {
        const existingCharges = estimate?.charges || []
        updatedCharges.forEach((newCharge) => {
          if (
            !existingCharges.find((existingCharge) => {
              return (
                existingCharge.cptCode === newCharge.cptCode &&
                existingCharge.chargeCode === newCharge.chargeCode &&
                existingCharge.description === newCharge.description &&
                existingCharge.revCode === newCharge.revCode
              )
            })
          ) {
            existingCharges.push(newCharge)
          }
        })
        if (updatedCharges.length) {
          hasChanges = true
        }
        finalCharges = existingCharges
        this.setEstimate({ ...estimate, charges: finalCharges })
      }
      this.setChargesOnForm(finalCharges)

      if (hasChanges) {
        this.chargesLoaded$.next()
      }

      if (this.hasAppointmentIdsChanged(estimate?.appointmentIds)) {
        this.markDirty()
      } else {
        this.clearDirty()
      }
    }),
    shareReplay(1),
  )

  templates$ = combineLatest([
    this.templateFilterCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
    ),
    this.estimateForm.controls.facilityId.valueChanges.pipe(
      filter(Boolean),
      distinctUntilChanged(),
    ),
    this.selectedTemplateCategoryId$.pipe(distinctUntilChanged()),
  ]).pipe(
    takeUntil(this.destroy$),
    debounceTime(300),
    switchMap(([name, facilityId, categoryId]) => {
      // Normalize the template name for filtering
      const normalizedName = (name ?? '').trim()
      const query: {
        categoryId: string
        all?: boolean
        pageIndex?: number
        pageSize?: number
        name?: string
      } = {
        categoryId,
      }

      if (normalizedName) {
        query.name = normalizedName
        query.pageIndex = 0
        query.pageSize = 100
      } else {
        query.all = true
      }

      return this.estimateTemplateApiService.getEstimateTemplatesCollection(query).pipe(
        map((templates) => templates.filter((template) => {
          if (!facilityId || !template.facilityIds?.length) {
            return true
          }

          return template.facilityIds.includes(facilityId)
        })),
      )
    },
    ),
    startWith([]),
  )

  templateCategories$ = this.estimateForm.controls.facilityId.valueChanges.pipe(
    startWith(this.estimateForm.controls.facilityId.value),
    filter(Boolean),
    distinctUntilChanged(),
    switchMap(() => this.estimateTemplateApiService.getEstimateCategories()),
    startWith([]),
    shareReplay(1),
  )

  claimSearchEnabled$ = combineLatest([
    this.clinicService.clinic$,
    this._permissionsService.hasPermission('estimate.model-claim-search'),
    this.estimateForm.controls.facilityId.valueChanges.pipe(
      map(
        (facilityId) =>
          this._facilityService.allFacilities.find(
            (f) => f.id === facilityId,
          ) || null,
      ),
      startWith(null as Facility | null),
    ),
  ]).pipe(
    map(([clinic, hasPermission, facility]) => {
      const hasFacilityConfig =
        !!facility?.mmx?.hbFacilityId || !!facility?.mmx?.pbFacilityId
      return (
        !!clinic?.pricer?.claimSearch && !!hasPermission && hasFacilityConfig
      )
    }),
    startWith(false),
    tap((enabled) => this.claimSearchEnabledChanged$.next(enabled)),
  )

  /**
   * Returns true only when the selected insurance meaningfully changes.
   *
   * This prevents redundant re-emissions from `insurances$` refreshes (such as
   * adding/removing a different insurance type) from re-triggering benefit
   * streams and overwriting benefit edits.
   */
  private shouldUpdateInsuranceSelection(
    currentInsurance: PatientInsurance | null | undefined,
    nextInsurance: PatientInsurance | null,
  ) {
    if (currentInsurance === null && nextInsurance === null) {
      return false
    }
    if (!currentInsurance || !nextInsurance) {
      return true
    }

    return currentInsurance.id !== nextInsurance.id || currentInsurance.eligibilityId !== nextInsurance.eligibilityId
  }

  /**
   * Accounts of the selected appointments, when per-visit resolution is on. Null when the flag
   * is off, so isInsuranceRelevantToAnyAccount scoping becomes a no-op. Mirrors
   * EstimateInsuranceLoader.relevantAccountIds().
   */
  private selectedAccountIds$: Observable<Set<string> | null> = combineLatest([
    this.estimateForm.controls.appointmentIds.valueChanges.pipe(
      startWith(this.estimateForm.controls.appointmentIds.value ?? []),
    ),
    this.appointmentOptions$,
    // ClinicService.clinic is static and can race insurances$/appointmentOptions$ on reload -
    // seed with its current value so an already-loaded clinic isn't ignored.
    this.clinicService.clinic$.pipe(startWith(this.clinicService.clinic)),
  ]).pipe(
    map(([appointmentIds, options, clinic]) => {
      if (!isPerVisitInsuranceSelectionEnabled(clinic) || !appointmentIds?.length) {
        return null
      }
      const accountIds = new Set(
        options
          .filter(option => appointmentIds.includes(option.value))
          .map(option => option.appointment.accountId)
          .filter((id): id is string => !!id),
      )
      return accountIds.size > 0 ? accountIds : null
    }),
    shareReplay(1),
  )

  /**
   * Resolves each insurance's role via getAccountType - same as insurances-card/
   * appointment-eligibility-card/estimate-insurance-loader.service.ts - so a record shared
   * across visits isn't treated as this visit's role based on another visit's. Only well-defined
   * for a single selected account; multi-visit selections fall back to the raw shared type.
   */
  private accountAwareTypeAccessor(accountIds: Set<string> | null): (insurance: PatientInsurance) => PatientInsuranceType | undefined {
    const perVisitEnabled = isPerVisitInsuranceSelectionEnabled(this.clinicService.clinic)
    const singleAccountId = accountIds?.size === 1 ? [...accountIds][0] : undefined

    return insurance => insurance.getAccountType(singleAccountId, perVisitEnabled)
  }

  private createInsuranceTypeStream(
    insuranceType: PatientInsuranceType,
    target$: BehaviorSubject<PatientInsurance | null | undefined>,
  ) {
    return combineLatest([
      this.patientDataService.insurances$,
      this.selectedAccountIds$,
    ]).pipe(
      takeUntil(this.destroy$),
      map(([insurances, accountIds]) => {
        // getAccountType() alone can't disambiguate two same-typed records from different accounts
        const scoped = insurances.filter(i => isInsuranceRelevantToAnyAccount(i, accountIds))
        return selectableInsurances(scoped, insuranceType, this.accountAwareTypeAccessor(accountIds))
      }),
      tap((options) => {
        this.estimate$.pipe(take(1)).subscribe((estimate) => {
          const slot = insuranceType === PatientInsuranceType.PRIMARY ? 'P' : 'S'
          const savedInsuranceId = estimate?.insurances?.[slot]?.insuranceId ?? null
          const currentInsurance = target$.value
          const selectedInsurance = resolveSelectedInsurance(options, currentInsurance, savedInsuranceId)
          if (this.shouldUpdateInsuranceSelection(currentInsurance, selectedInsurance)) {
            target$.next(selectedInsurance)
          }
        })
      }),
    )
  }

  primaryInsurances$ = this.createInsuranceTypeStream(
    PatientInsuranceType.PRIMARY,
    this.primaryInsurance$,
  )

  secondaryInsurances$ = this.createInsuranceTypeStream(
    PatientInsuranceType.SECONDARY,
    this.secondaryInsurance$,
  )

  resetEstimateFormEvent(type: string = null, estimate: EstimateData) {
    switch (type) {
      case 'template':
        if (estimate.templateId) {
          this._crudService
            .read(EstimateTemplateModel, estimate.templateId)
            .subscribe((template) => {
              if (template) {
                const facility = this._facilityService.allFacilities.find(
                  (f) =>
                    !template.facilityIds ||
                    template.facilityIds.includes(f.id),
                )
                if (facility) {
                  this.estimateForm.patchValue({ facilityId: facility.id })
                  this.setEstimate({ ...estimate, facilityId: facility.id })
                }
                const charges = this._chargeFormService.ensureVisitNumbers(
                  template.charges || [],
                  this.defaultVisitNumbers(),
                )
                this.setEstimate({
                  ...estimate,
                  charges,
                })
                this.estimateForm.patchValue({
                  facilityId: template.facilityIds
                    ? template.facilityIds[0]
                    : null,
                })
                this.setChargesOnForm(charges)
                this.markDirty()
              }
            })
        }
        break
      case 'estimate':
        if (estimate.id) {
          this._crudService
            .read(EstimateModel, estimate.id)
            .subscribe((est) => {
              if (est) {
                if (est.facilityId) {
                  const facility = this._facilityService.allFacilities.find(
                    (f) => f.id === est.facilityId,
                  )
                  if (facility) {
                    this.estimateForm.patchValue({ facilityId: facility.id })
                  }
                  this.setEstimate({
                    ...estimate,
                    facilityId: est.facilityId,
                  })
                }
                const charges = est.charges || []
                this.setEstimate({ ...estimate, charges })
                this.estimateForm.patchValue({
                  facilityId: est.facilityId,
                })
                this.setChargesOnForm(charges)
                this.markDirty()
              }
            })
        }
        break
      case 'clear':
        this.deleteAllChargesEvent(estimate)
        break
      default:
        this.addChargesByAppointments(true)
        break
    }
  }

  addChargesByAppointments(reset?: boolean) {
    this.resetByAppointments$.next(reset ?? false)
    const appointmentIdsControl = this.estimateForm.controls.appointmentIds
    const selectedAppointmentIds = appointmentIdsControl.value ?? []

    if (reset !== undefined) {
      // Force a valueChanges emission so reset/reapply actions reload charges even when the selection is unchanged.
      appointmentIdsControl.setValue([...selectedAppointmentIds])
    }
  }

  addClaim(claim: { charges: EstimateCharge[] }) {
    if (claim) {
      this.estimate$.pipe(first()).subscribe((currentEstimate) => {
        this.setEstimate({ ...currentEstimate, charges: claim.charges })
        this.setChargesOnForm(claim.charges)
        this.markDirty()
      })
    }
  }

  markDirty() {
    this.needsPricing$.next(true)
  }

  deleteAllChargesEvent(estimate: EstimateData) {
    this.setEstimate({ ...estimate, charges: [], templateId: null })
    this.estimateForm.controls.charges.clear()
    this.estimateForm.get('templateId')?.setValue(null)
    this.markDirty()
  }

  clearDirty() {
    this.needsPricing$.next(false)
  }

  /**
   * Resets all state in the service. Should be called when leaving the estimate screen.
   */
  resetState() {
    this.estimateLoading$.next(true)
    this.primaryInsurance$.next(null)
    this.secondaryInsurance$.next(null)
    this.needsPricing$.next(false)
    this.estimate$.next(null)
    this.resetByAppointments$.next(false)
    this.chargesLoading$.next(false)
    this.modelClaimSearchQuery$.next(null)
    this.templateFilterCtrl.reset('')
    this.selectedTemplateCategoryId$.next('root')
    this.visitNumbersValid = true
    this.originalBenefits$.next(null)
    this.originalSecondaryBenefits$.next(null)
    this.primaryBenefitsLoading$.next(false)
    this.secondaryBenefitsLoading$.next(false)
    this.needsPricingNotice?.dismiss()
    this.needsPricingNotice = undefined

    // Reset form to initial state
    this.estimateForm.reset({
      facilityId: null,
      templateId: null,
      saveTemplate: false,
      name: null,
      episodeId: null,
      appointmentIds: [],
      patientType: Estimate.PATIENT_TYPE.OutPatient,
    })
    this.estimateForm.controls.charges.clear()
  }

  setTemplateCategory(categoryId: string | null) {
    this.selectedTemplateCategoryId$.next(categoryId ?? 'root')
  }

  setPrimaryInsurance(insurance: PatientInsurance | null) {
    this.primaryInsurance$.next(insurance)
  }

  setSecondaryInsurance(insurance: PatientInsurance | null) {
    this.secondaryInsurance$.next(insurance)
  }

  setSelectedAppointmentIds(ids: string[]) {
    this.estimateForm.controls.appointmentIds.setValue([...ids])
  }

  setEstimateFacilityId(id: string | null): void {
    const previous = this.estimate$.value
    if (!previous) {
      return
    }
    // Guard the no-op: mmx-estimate-charges' facilityId.valueChanges subscription
    // re-enters onFacilityChange whenever setValue(null) re-emits, which would
    // push a fresh estimate$ reference every ~100ms and trap the summary's
    // debounced (500ms) _estimate writer in an unbounded reset cycle.
    if ((previous.facilityId ?? null) === (id ?? null)) {
      return
    }
    this.estimate$.next({ ...previous, facilityId: id })
    if (previous.facilityId && previous.facilityId !== id) {
      this.markDirty()
    }
  }

  setEstimateTemplateId(id: string | null): void {
    const previous = this.estimate$.value
    if (!previous) {
      return
    }
    if ((previous.templateId ?? null) === (id ?? null)) {
      return
    }
    this.estimate$.next({ ...previous, templateId: id })
    this.estimateForm.get('templateId')?.setValue(id)
  }

  clearEstimateCharges(): void {
    const previous = this.estimate$.value
    if (!previous) {
      return
    }
    this.estimate$.next({ ...previous, charges: [], templateId: null })
    this.estimateForm.controls.charges.clear()
    this.estimateForm.get('templateId')?.setValue(null)
    this.markDirty()
  }

  setEstimate(estimate: EstimateModel | EstimateData | null) {
    if (estimate instanceof EstimateModel) {
      this.estimate$.next(this.estimateModelToEstimateData(estimate))
    } else {
      this.estimate$.next(estimate)
    }
  }

  estimateModelToEstimateData(estimate: EstimateModel): EstimateData {
    // @TODO: why are we converting to JSON here? This loses methods on the EstimateModel instance.
    return {
      ...estimate.toJSON(),
      invoices: estimate.invoices ? estimate.invoices.map((inv) => inv.toJSON?.()) : undefined,
      output: estimate.output,
      pbRequests: this.mapToArray(estimate.pbRequests),
      hbRequests: this.mapToArray(estimate.hbRequests),
      pbResponses: this.mapToArray(estimate.pbResponses),
      hbResponses: this.mapToArray(estimate.hbResponses),
    }

  }

  /**
   * Initializes the estimate form with values from the provided estimate.
   * Should be called after setEstimate() to populate the form.
   * @param estimate The estimate to initialize the form with
   * @param isNew Whether this is a new estimate (triggers appointment-based charge loading)
   */
  initializeEstimateForm(
    estimate: EstimateModel | EstimateData,
    isNew: boolean,
  ) {
    if (!estimate) {
      return
    }
    const estimateData: EstimateData =
      estimate instanceof EstimateModel
        ? {
          ...estimate.toJSON(),
          invoices: estimate.invoices
            ? estimate.invoices.map((inv) => inv.toJSON?.())
            : undefined,
          output: estimate.output,
          pbRequests: this.mapToArray(estimate.pbRequests),
          hbRequests: this.mapToArray(estimate.hbRequests),
          pbResponses: this.mapToArray(estimate.pbResponses),
          hbResponses: this.mapToArray(estimate.hbResponses),
        }
        : estimate

    this.estimateForm.patchValue({
      facilityId: estimateData.facilityId ?? null,
      templateId: estimateData.templateId,
      appointmentIds: estimateData.appointmentIds ?? [],
      patientType: estimateData.patientType ?? Estimate.PATIENT_TYPE.OutPatient,
    })

    if (estimateData.charges) {
      this.setChargesOnForm(estimateData.charges)
    }

    if (isNew) {
      this.resetEstimateFormEvent(undefined, estimateData)
    }
  }

  mapToArray<K, V>(map: Map<K, V> | undefined): Array<[K, V]> | undefined {
    return map ? Array.from(map.entries()) : undefined
  }

  /**
   * Checks if the current appointment IDs in the form differ from the original values.
   * @param originalIds - The original appointment IDs from the estimate
   * @returns True if the appointment IDs have changed, false otherwise
   */
  private hasAppointmentIdsChanged(originalIds: string[] | undefined): boolean {
    const currentIds = this.estimateForm.controls.appointmentIds.value ?? []
    const original = originalIds ?? []
    return (
      currentIds.length !== original.length ||
      currentIds.some((id) => !original.includes(id))
    )
  }

  /**
   * Sets the charges on the form without replacing the existing FormArray.
   * Keeping the original control instance preserves the valueChanges
   * subscription that keeps estimate$ in sync with the form.
   * @param charges Array of EstimateCharge objects
   */
  private setChargesOnForm(charges: EstimateCharge[]): void {
    const chargesWithVisitNumbers = this._chargeFormService.ensureVisitNumbers(
      charges,
      this.defaultVisitNumbers(),
    )
    this._chargeFormService.setChargesOnFormArray(
      this.estimateForm.controls.charges,
      chargesWithVisitNumbers,
    )
  }

  /** Visit numbers of the appointments in `appointmentIds`, ascending. */
  private selectedVisitNumbers(
    options: AppointmentSelectOption[],
    appointmentIds: string[],
  ): number[] {
    return options
      .filter((option) => appointmentIds.includes(option.value))
      .map((option) => option.visitNumber)
      .filter((visitNumber): visitNumber is number => visitNumber != null)
      .sort((a, b) => a - b)
  }

  /**
   * Every selected appointment's visit number, read from `appointmentOptions$` so they always match
   * an option the visit number picker renders. They are positional (index in the patient's
   * appointment list), so a single-appointment estimate is not necessarily visit 1. Falls back to
   * the appointment count while options are still loading, so `ensureVisitNumbers` keeps its
   * sequential default instead of collapsing to [1].
   */
  private defaultVisitNumbers(): number | number[] {
    const appointmentIds = this.estimateForm.controls.appointmentIds.value ?? []
    const visitNumbers = this.selectedVisitNumbers(this.appointmentOptionsCache, appointmentIds)
    return visitNumbers.length > 0 ? visitNumbers : appointmentIds.length
  }

  /**
   * Loads a template by ID and applies it to the current estimate.
   * @param templateId The ID of the template to load
   */
  loadTemplate(templateId: string): void {
    if (!templateId) {
      return
    }
    this.estimate$
      .pipe(
        first(),
        switchMap((estimate) => {
          if (estimate) {
            estimate.templateId = templateId
          }

          return this._crudService.read(EstimateTemplateModel, templateId).pipe(
            first(),
            filter((template) => !!template),
            tap((template) => {
              const facility = this._facilityService.allFacilities.find(
                (f) =>
                  !template.facilityIds || template.facilityIds.includes(f.id),
              )
              if (facility) {
                this.estimateForm.patchValue({ facilityId: facility.id })
                if (estimate) {
                  estimate.facilityId = facility.id
                }
              }

              const charges = this._chargeFormService.ensureVisitNumbers(
                template.charges || [],
                this.defaultVisitNumbers(),
              )
              if (estimate) {
                estimate.charges = charges
              }
              this.setChargesOnForm(charges)
              this.markDirty()
            }),
          )
        }),
      )
      .subscribe()
  }

  /**
   * Adds discount form controls to a charge at the specified index.
   * @param index The index of the charge in the charges array
   * @param discountType The type of discount ('percentage' or 'fixed')
   */
  onAddChargeDiscountControl(
    index: number,
    discountType: EstimateChargeDiscountType,
  ): void {
    this._chargeFormService.addDiscountControls(
      this.estimateForm.controls.charges,
      index,
      discountType,
    )
  }

  /**
   * Removes discount form controls from a charge at the specified index.
   * @param index The index of the charge in the charges array
   */
  onRemoveChargeDiscountControl(index: number): void {
    this._chargeFormService.removeDiscountControls(
      this.estimateForm.controls.charges,
      index,
    )
    this.markDirty()
  }

  /**
   * Gets the current charges from the form, converted to EstimateCharge array.
   * @returns Array of EstimateCharge objects from the current form values
   */
  getChargesFromForm(): EstimateCharge[] {
    return this._chargeFormService.getChargesFromFormArray(
      this.estimateForm.controls.charges,
    )
  }

  openPricingNotice() {
    if (this.needsPricingNotice) {
      return
    }
    this.needsPricingNotice = this.snackBar.open('Changes have been made. Please reprice the estimate.', 'Calculate & Save', {
      duration: 0,
      verticalPosition: 'top',
    })
    this.needsPricingNotice?.onAction()
      .pipe(take(1))
      .subscribe(() => {
        this.pricingNoticeAction$.next()
        this.needsPricingNotice = undefined
      })
  }
}
